import { Heading, IconButton, Tooltip, Text, Divider } from '../../Ant';
import { ArrowLeft, Delete, Home, LinearCross, Search, Timer } from '../../icons';
import { useTranslation } from 'react-i18next';
import { Badge, Flex } from 'antd';
import { ROUTES, UserType } from '../../../constants/constants';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';
import { useFeatureContext } from '../../../context/FeatureContext';
import { FeatureSelectionTab } from '../../../interface/featureSelection';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useClearCreateContexts } from '../../../hooks/useClearCreateContexts';
import NotificationBellButton from '../../notificationBellButton/notificationBellButton';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { AnalyticsEvent, FeatureButtons } from '../../../interface/analytics';
import { useBadgeCounts } from '../../../hooks/useBadgeCounts';
import AppNavigationMenu from '../../appNavigationMenu/appNavigationMenu';

const TransactionHeaderNavigation = ({
  handleBack,
  handleHome,
  showCross,
  showBackWithCross,
  showHomeWithBackButton,
  validateOnFormChanged,
}: {
  handleBack?: () => void;
  handleHome?: () => void;
  showCross: boolean;
  showBackWithCross?: boolean;
  showHomeWithBackButton: boolean;
  validateOnFormChanged?: (cb: () => void) => void;
}) => {
  const { t } = useTranslation();
  const { clear, push } = useNavigationStack();
  const clearCreateContexts = useClearCreateContexts();

  const navigateToDashboard = () => {
    clear();
    clearCreateContexts();
    push(ROUTES.DASHBOARD);
  };

  return (
    <>
      {handleBack && (!showCross || showBackWithCross) && (
        <Flex gap={5}>
          {showHomeWithBackButton && !showCross && (
            <>
              <Tooltip title={t('common:home')} placement="top" arrow={false}>
                <IconButton
                  icon={<Home />}
                  onClick={
                    validateOnFormChanged
                      ? () => validateOnFormChanged(navigateToDashboard)
                      : navigateToDashboard
                  }
                  data-testid="transaction-header-home-button"
                  aria-label={t('common:home')}
                  size="large"
                />
              </Tooltip>
              <Divider type="vertical" />
            </>
          )}

          <Tooltip title={t('common:back')} placement="top" arrow={false}>
            <IconButton
              icon={<ArrowLeft />}
              onClick={handleBack}
              data-testid="transaction-header-back-button"
              aria-label={t('common:back')}
              size="large"
            />
          </Tooltip>
        </Flex>
      )}
      {handleHome && (
        <Tooltip title={t('common:home')} placement="top" arrow={false}>
          <IconButton
            icon={<Home />}
            onClick={handleHome}
            data-testid="transaction-header-home-button"
            aria-label={t('common:home')}
            size="large"
          />
        </Tooltip>
      )}
    </>
  );
};

const TransactionHeaderTimerButton = ({
  timerBadgeCount,
}: {
  timerBadgeCount: number | string;
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { clear, push } = useNavigationStack();
  const { trackAction } = useAnalytics();

  return (
    <Tooltip title={t('common:timers.timer')} placement="top" arrow={false}>
      <Badge count={location.pathname === ROUTES.TIMER_LIST ? 0 : timerBadgeCount} offset={[-7, 8]}>
        <IconButton
          icon={<Timer />}
          onClick={() => {
            trackAction(AnalyticsEvent.BUTTON_CLICK, FeatureButtons.TIMER);
            clear();
            push(ROUTES.TIMER_LIST);
          }}
          size="large"
          data-testid="timer-button"
          aria-label={t('common:timers.timer')}
          className={location.pathname === ROUTES.TIMER_LIST ? 'page-active' : ''}
        />
      </Badge>
    </Tooltip>
  );
};

const TransactionHeaderConflictSearchButton = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { userDetails } = useUser();
  const { features } = useFeatureContext();
  const { clear, push } = useNavigationStack();
  const { trackAction } = useAnalytics();

  const canSearchConflicts =
    userDetails?.userType !== UserType.RESTRICTED &&
    !!features[String(FeatureSelectionTab.conflictSearch)];

  if (!canSearchConflicts) return null;

  return (
    <Tooltip title={t('conflict:conflictSearch')} placement="top" arrow={false}>
      <IconButton
        icon={<Search />}
        data-testid="conflict-search-button"
        aria-label={t('conflict:conflictSearch')}
        className={location.pathname === ROUTES.CONFLICT_SEARCH ? 'page-active' : ''}
        onClick={() => {
          trackAction(AnalyticsEvent.BUTTON_CLICK, FeatureButtons.CONFLICT_SEARCH);
          clear();
          push(ROUTES.CONFLICT_SEARCH);
        }}
        size="large"
      />
    </Tooltip>
  );
};

const AddEditTransactionHeader = ({
  handleBack,
  handleClose,
  handleDelete,
  handleHome,
  title,
  subTitle,
  showSettings = false,
  showMenu = false,
  showCross = false,
  showBackWithCross = false,
  showHomeWithBackButton = true,
  renderTimer = () => null,
  validateOnFormChanged,
}: {
  handleBack?: () => void;
  handleClose?: () => void;
  handleHome?: () => void;
  handleDelete?: () => void;
  title: string;
  subTitle?: string;
  showSettings?: boolean;
  showMenu?: boolean;
  showCross?: boolean;
  showBackWithCross?: boolean;
  showHomeWithBackButton?: boolean;
  renderTimer?: () => React.ReactNode;
  validateOnFormChanged?: (cb: () => void) => void;
}) => {
  const { t } = useTranslation();
  const { notificationBadgeCount, timerBadgeCount } = useBadgeCounts();

  return (
    <Flex gap={5} align="center" className="w-full">
      <TransactionHeaderNavigation
        handleBack={handleBack}
        handleHome={handleHome}
        showCross={showCross}
        showBackWithCross={showBackWithCross}
        showHomeWithBackButton={showHomeWithBackButton}
        validateOnFormChanged={validateOnFormChanged}
      />
      <div className="d-flex justify-between items-center w-full min-w-0">
        <Flex vertical className="min-w-0">
          <Heading
            $fontsize="14px"
            $color="title"
            $weight={600}
            $lineheight="21px"
            className="lineclamp-1"
            data-testid="transaction-header-title"
          >
            {title}
          </Heading>
          <Text
            $fontsize="10px"
            $color="description"
            $weight={500}
            $lineheight="130%"
            className="lineclamp-1"
            data-testid="transaction-header-subtitle"
          >
            {subTitle}
          </Text>
        </Flex>
        <Flex gap={8} align="items-center" className="flex-shrink-0">
          {handleDelete && (
            <Tooltip title={t('transaction:delete')} placement="top" arrow={false}>
              <IconButton
                icon={<Delete className="delete-icon" />}
                onClick={handleDelete}
                data-testid="transaction-header-delete-button"
                aria-label={t('transaction:delete')}
                size="large"
              />
            </Tooltip>
          )}
          {showCross && (
            <IconButton
              icon={<LinearCross />}
              onClick={handleClose ?? handleBack}
              data-testid="transaction-header-cross-button"
              aria-label={t('common:bottomNavigation.close')}
              size="large"
            />
          )}
          {showSettings && <TransactionHeaderTimerButton timerBadgeCount={timerBadgeCount} />}
          {renderTimer()}
          {showMenu && <TransactionHeaderConflictSearchButton />}
          {showSettings && (
            <NotificationBellButton notificationBadgeCount={notificationBadgeCount} />
          )}
          {showMenu && <AppNavigationMenu />}
        </Flex>
      </div>
    </Flex>
  );
};

export default AddEditTransactionHeader;
